/**
 * Doc-embedded provenance manifest — pass-through codec.
 *
 * Companion to the Spec 18 run-manifest codec (``runManifest.ts``)
 * but for the Asthra **provenance** manifest: per-section
 * generation metadata, content hashes, and the agent_edit audit
 * log written by ``ProvenanceReconciler`` on the Python side.
 *
 * SRP: this module is the TypeScript boundary for the provenance
 * Custom XML Part. It does **not** parse the XML. The Python side
 * owns the schema and the lxml-based parser; we just shuttle the
 * raw XML string between Word and the WebSocket bridge.
 *
 * # DECISION: Pass-through, no client-side parse.
 * # WHY: The provenance schema is materially more complex than the
 * #      run-manifest schema (nested ``<section>``+``<agent_edit>``
 * #      with ~15 attrs each, plus ``<orphaned_edits>``). A regex
 * #      decoder would be brittle and DOMParser would split test
 * #      coverage between two languages. Python already owns the
 * #      canonical encoder/decoder; mirroring it in TypeScript is
 * #      maintenance debt for zero user-facing benefit.
 * # DATE: 2026-05-28
 *
 * # DECISION: Separate namespace from the run-manifest namespace.
 * # WHY: Two distinct concepts (resume pointer vs. generation
 * #      provenance) must be addressable independently — Office.js
 * #      ``getByNamespaceAsync`` is the only filter we get.
 * # DATE: 2026-05-28
 */

// Fixed namespace URI for the provenance manifest. MUST stay in sync
// with ``ASTHRA_NS`` in ``src/csr_sim/docx_manifest.py``. A schema
// rev would bump this; legacy docs would then fall through to
// "no provenance" instead of being misparsed.
export const PROVENANCE_NAMESPACE = "http://asthra.ai/csr/manifest";

// Root element name used by the Python serialiser. Useful for the
// minimal sanity check below; not a parser.
export const PROVENANCE_ROOT = "manifest";

/**
 * Lightweight shape check. Returns true if ``xml`` looks like a
 * provenance manifest from this app: a string whose root element
 * name is ``manifest`` and whose declared namespace matches.
 *
 * Used by the Office.js adapter to reject foreign CustomXmlParts
 * that happen to live under the same namespace prefix. Not a
 * security boundary — just a defensive sanity check. The Python
 * decoder is authoritative.
 */
export function looksLikeProvenanceManifest(xml: string | null | undefined): boolean {
  if (!xml || typeof xml !== "string") return false;
  // The Python serialiser uses a namespace prefix:
  //   <asthra:manifest xmlns:asthra="http://asthra.ai/csr/manifest" ...>
  // rather than a default namespace declaration. Accept either form.
  const hasRootTag =
    xml.includes(`<${PROVENANCE_ROOT}`) || // <manifest ...
    xml.includes(`:${PROVENANCE_ROOT}`) || // <asthra:manifest ...
    xml.includes(`<asthra:${PROVENANCE_ROOT}`); // explicit prefix form
  const hasNamespace =
    xml.includes(`xmlns="${PROVENANCE_NAMESPACE}"`) || // default ns
    xml.includes(`xmlns:asthra="${PROVENANCE_NAMESPACE}"`); // prefixed ns
  return hasRootTag && hasNamespace;
}
